// Canvas renderer. DPR-aware, with a camera centred on the self player and a
// zoom that scales with screen size: small screens (phones) zoom OUT so they
// see a fair slice of the world instead of a tiny patch, while desktops render
// ~1:1. Everything is drawn in WORLD coordinates via a single camera transform,
// so positions and sizes scale uniformly with the zoom.

import { PLAYER_RADIUS, MONSTER_RADIUS, HEAL_NODES, HEAL_RADIUS } from "@mmo/shared";
import type { WorldState, ViewEntity } from "./state";

const GRID = 200;
const BG = "#0a0d12";
const GRID_COLOR = "rgba(255,255,255,0.045)";
const BORDER_COLOR = "rgba(124,164,255,0.55)";
const LABEL_FONT = "13px system-ui, -apple-system, sans-serif";

// Zoom: the smaller screen dimension maps to ~ZOOM_REF world units at zoom 1.
// Smaller screens get a smaller zoom (see more); never zoom in past 1:1.
const ZOOM_REF = 700;
const MIN_ZOOM = 0.55;

export interface Projectile {
  x: number;
  y: number;
  targetId: number;
}

export class Renderer {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;

  private cssW = 0;
  private cssH = 0;
  private dpr = 1;
  private zoom = 1;

  // Camera: world coordinate at the top-left of the viewport, and the world
  // span visible (cssW/zoom × cssH/zoom).
  private camX = 0;
  private camY = 0;
  private viewW = 0;
  private viewH = 0;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext("2d", { alpha: false });
    if (!ctx) throw new Error("2D canvas context unavailable");
    this.ctx = ctx;
    this.resize();
    window.addEventListener("resize", this.resize);
    window.addEventListener("orientationchange", this.resize);
  }

  private resize = (): void => {
    this.dpr = Math.min(window.devicePixelRatio || 1, 3);
    this.cssW = this.canvas.clientWidth || window.innerWidth;
    this.cssH = this.canvas.clientHeight || window.innerHeight;
    this.canvas.width = Math.round(this.cssW * this.dpr);
    this.canvas.height = Math.round(this.cssH * this.dpr);
    const fit = Math.min(this.cssW, this.cssH) / ZOOM_REF;
    this.zoom = Math.max(MIN_ZOOM, Math.min(1, fit));
  };

  /** Convert a canvas-space (CSS px) point to world space. */
  screenToWorld(sx: number, sy: number): { x: number; y: number } {
    return { x: this.camX + sx / this.zoom, y: this.camY + sy / this.zoom };
  }

  draw(
    world: WorldState,
    selfX: number,
    selfY: number,
    marker: { x: number; y: number } | null,
    ringAt: { x: number; y: number } | null,
    projectiles: Projectile[],
  ): void {
    const ctx = this.ctx;
    const z = this.zoom;
    this.viewW = this.cssW / z;
    this.viewH = this.cssH / z;

    const halfW = world.worldW / 2;
    const halfH = world.worldH / 2;
    this.camX = clamp(selfX - this.viewW / 2, -halfW, Math.max(-halfW, halfW - this.viewW));
    this.camY = clamp(selfY - this.viewH / 2, -halfH, Math.max(-halfH, halfH - this.viewH));

    // Background in CSS pixels.
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.fillStyle = BG;
    ctx.fillRect(0, 0, this.cssW, this.cssH);

    // World transform: world coords -> device pixels (with zoom + camera).
    ctx.setTransform(this.dpr * z, 0, 0, this.dpr * z, -this.camX * this.dpr * z, -this.camY * this.dpr * z);

    this.drawGrid();
    this.drawBorder(world.worldW, world.worldH);
    for (const n of HEAL_NODES) this.drawHealNode(n.x, n.y);
    if (marker) this.drawMarker(marker.x, marker.y);
    if (ringAt) this.drawReticle(ringAt.x, ringAt.y);

    for (const m of world.money.values()) this.drawMoney(m.x, m.y);

    for (const e of world.entities.values()) {
      if (e.id === world.selfId) continue;
      this.drawEntity(e.renderX, e.renderY, e, false);
    }
    const self = world.self();
    if (self) this.drawEntity(selfX, selfY, self, true);

    for (const p of projectiles) this.drawFireball(p.x, p.y);
  }

  private drawGrid(): void {
    const ctx = this.ctx;
    ctx.lineWidth = 1 / this.zoom;
    ctx.strokeStyle = GRID_COLOR;
    ctx.beginPath();
    const left = this.camX;
    const top = this.camY;
    const right = this.camX + this.viewW;
    const bottom = this.camY + this.viewH;
    for (let x = Math.floor(left / GRID) * GRID; x < right; x += GRID) {
      ctx.moveTo(x, top);
      ctx.lineTo(x, bottom);
    }
    for (let y = Math.floor(top / GRID) * GRID; y < bottom; y += GRID) {
      ctx.moveTo(left, y);
      ctx.lineTo(right, y);
    }
    ctx.stroke();
  }

  private drawBorder(worldW: number, worldH: number): void {
    const ctx = this.ctx;
    ctx.lineWidth = 2 / this.zoom;
    ctx.strokeStyle = BORDER_COLOR;
    ctx.strokeRect(-worldW / 2, -worldH / 2, worldW, worldH);
  }

  private drawMarker(wx: number, wy: number): void {
    const ctx = this.ctx;
    ctx.lineWidth = 2 / this.zoom;
    ctx.strokeStyle = "rgba(120,200,120,0.9)";
    ctx.beginPath();
    ctx.arc(wx, wy, 10, 0, Math.PI * 2);
    ctx.moveTo(wx + 4, wy);
    ctx.arc(wx, wy, 4, 0, Math.PI * 2);
    ctx.stroke();
  }

  private drawReticle(wx: number, wy: number): void {
    const ctx = this.ctx;
    ctx.lineWidth = 2 / this.zoom;
    ctx.strokeStyle = "rgba(255,210,90,0.9)";
    ctx.beginPath();
    ctx.arc(wx, wy, MONSTER_RADIUS + 7, 0, Math.PI * 2);
    ctx.stroke();
  }

  private drawHealNode(wx: number, wy: number): void {
    const ctx = this.ctx;
    if (this.offscreen(wx, wy, HEAL_RADIUS + 8)) return;
    ctx.beginPath();
    ctx.arc(wx, wy, HEAL_RADIUS, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(80,200,120,0.12)";
    ctx.fill();
    ctx.lineWidth = 2 / this.zoom;
    ctx.strokeStyle = "rgba(90,220,140,0.8)";
    ctx.stroke();
    ctx.lineWidth = 4;
    ctx.strokeStyle = "rgba(120,240,160,0.95)";
    ctx.beginPath();
    ctx.moveTo(wx - 12, wy);
    ctx.lineTo(wx + 12, wy);
    ctx.moveTo(wx, wy - 12);
    ctx.lineTo(wx, wy + 12);
    ctx.stroke();
  }

  private drawMoney(wx: number, wy: number): void {
    const ctx = this.ctx;
    if (this.offscreen(wx, wy, 16)) return;
    ctx.beginPath();
    ctx.arc(wx, wy, 7, 0, Math.PI * 2);
    ctx.fillStyle = "#f2c14e";
    ctx.fill();
    ctx.lineWidth = 2 / this.zoom;
    ctx.strokeStyle = "rgba(120,90,10,0.8)";
    ctx.stroke();
  }

  private drawFireball(wx: number, wy: number): void {
    const ctx = this.ctx;
    if (this.offscreen(wx, wy, 16)) return;
    ctx.beginPath();
    ctx.arc(wx, wy, 6, 0, Math.PI * 2);
    ctx.fillStyle = "#ff7a2f";
    ctx.fill();
    ctx.lineWidth = 2 / this.zoom;
    ctx.strokeStyle = "rgba(255,220,150,0.95)";
    ctx.stroke();
  }

  private drawEntity(wx: number, wy: number, e: ViewEntity, isSelf: boolean): void {
    const ctx = this.ctx;
    const radius = e.kind === "monster" ? MONSTER_RADIUS : PLAYER_RADIUS;
    if (this.offscreen(wx, wy, radius + 28)) return;

    ctx.beginPath();
    ctx.arc(wx, wy, radius, 0, Math.PI * 2);
    ctx.fillStyle = e.color;
    ctx.fill();
    ctx.lineWidth = (isSelf ? 3 : 2) / this.zoom;
    ctx.strokeStyle = isSelf ? "#ffffff" : "rgba(0,0,0,0.55)";
    ctx.stroke();

    if (e.kind === "monster" || e.hp < e.maxHp) {
      this.drawHpBar(wx, wy - radius - 10, radius, e.hp, e.maxHp);
    }

    if (e.kind === "player") {
      const ly = wy - radius - (e.hp < e.maxHp ? 18 : 6);
      ctx.font = LABEL_FONT;
      ctx.textAlign = "center";
      ctx.textBaseline = "bottom";
      ctx.lineWidth = 3 / this.zoom;
      ctx.strokeStyle = "rgba(0,0,0,0.7)";
      ctx.strokeText(e.name, wx, ly);
      ctx.fillStyle = isSelf ? "#ffffff" : "#dbe5f1";
      ctx.fillText(e.name, wx, ly);
    }
  }

  private drawHpBar(wx: number, wy: number, radius: number, hp: number, maxHp: number): void {
    const ctx = this.ctx;
    const w = radius * 2;
    const h = 4;
    const x = wx - radius;
    const frac = Math.max(0, Math.min(1, hp / maxHp));
    ctx.fillStyle = "rgba(0,0,0,0.55)";
    ctx.fillRect(x - 1, wy - 1, w + 2, h + 2);
    ctx.fillStyle = "#3a3f47";
    ctx.fillRect(x, wy, w, h);
    ctx.fillStyle = frac > 0.5 ? "#57d977" : frac > 0.25 ? "#e3b341" : "#e5534b";
    ctx.fillRect(x, wy, w * frac, h);
  }

  /** World-space cull: is (wx,wy) outside the visible world rect (+ margin)? */
  private offscreen(wx: number, wy: number, margin: number): boolean {
    return (
      wx < this.camX - margin ||
      wy < this.camY - margin ||
      wx > this.camX + this.viewW + margin ||
      wy > this.camY + this.viewH + margin
    );
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
